'use client';

import type { LucideIcon } from 'lucide-react';
import type { OnboardingCockpitStatus } from 'src/features/advisor-book-onboarding/constants/cockpit-url-state';
import { Badge } from '@savvy-kit/components/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@savvy-kit/components/hover-card';
import { Text } from '@savvy-kit/foundations/text';
import { AlertTriangle, CheckCircle2, LoaderCircle, Mail, Send } from 'lucide-react';
import {
  COCKPIT_STATUS_COPY,
  statusHoverContent,
} from 'src/features/advisor-book-onboarding/constants/cockpit-status-copy';

type OnboardingCockpitStatusBadgeProps = {
  readonly status: OnboardingCockpitStatus;
  readonly blockedReason?: string | null;
  readonly accountsTransferred?: number;
  readonly accountsTotal?: number;
  readonly primaryClientName?: string | null;
  readonly lastContactedAt?: string | null;
  readonly className?: string;
};

const COCKPIT_STATUS_ICONS: Readonly<Record<OnboardingCockpitStatus, LucideIcon>> = {
  confirm_account: AlertTriangle,
  ready_to_send: Send,
  awaiting_client: Mail,
  in_progress: LoaderCircle,
  complete: CheckCircle2,
  blocked: AlertTriangle,
};

type HoverSectionProps = {
  readonly label: string;
  readonly body: string;
};

function HoverSection({ label, body }: HoverSectionProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text variant="overline" color="secondary">
        {label}
      </Text>
      <Text variant="body-sm" color="dark">
        {body}
      </Text>
    </div>
  );
}

export function OnboardingCockpitStatusBadge({
  status,
  blockedReason,
  accountsTransferred = 0,
  accountsTotal = 0,
  primaryClientName = null,
  lastContactedAt,
  className,
}: OnboardingCockpitStatusBadgeProps) {
  const { label, variant } = COCKPIT_STATUS_COPY[status];
  const Icon = COCKPIT_STATUS_ICONS[status];
  const hoverContent = statusHoverContent(status, {
    accountsTransferred,
    accountsTotal,
    primaryClientName,
    lastContactedAt,
    blockedReason,
  });

  const badge = (
    <Badge variant={variant} className={className}>
      <Icon aria-hidden="true" className={status === 'in_progress' ? 'animate-spin' : undefined} />
      {label}
    </Badge>
  );

  if (hoverContent === null) {
    return badge;
  }

  return (
    <HoverCard>
      <HoverCardTrigger
        render={
          <span
            role="group"
            className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            tabIndex={0}
            aria-label={`${label}. Now: ${hoverContent.now} Waiting on: ${hoverContent.waitingOn} Suggested next: ${hoverContent.suggestedNext}`}
          />
        }
      >
        {badge}
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" className="w-72 space-y-3">
        <HoverSection label="Now" body={hoverContent.now} />
        <HoverSection label="Waiting on" body={hoverContent.waitingOn} />
        <HoverSection label="Suggested next" body={hoverContent.suggestedNext} />
      </HoverCardContent>
    </HoverCard>
  );
}
